import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { marked } from 'marked'
import { data } from 'react-router'
import { type Route } from './+types/website-privacy-policy.ts'

const MetaTitle = 'Dealer Website Privacy Policy | Dealer Pro Edge'
const MetaDescription =
	'Explains how personal information is collected, used, shared, and protected on dealership websites powered by Dealer Pro Edge.'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/website-privacy-policy'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'robots', content: 'noindex, nofollow' },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
	]
}

// =============================================================================
// MARKDOWN CONTENT
// =============================================================================

const markdown = `# Privacy Policy for Dealer Pro Edge

**Last Updated:** April 15, 2026

This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.

We use Your Personal Data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.

## Interpretation and Definitions

### Interpretation
The words whose initial letters are capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.

### Definitions
For the purposes of this Privacy Policy:

- **Account** means a unique account created for You to access our Service or parts of our Service.
- **Affiliate** means an entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.
- **Company** (referred to as either "the Company", "We", "Us" or "Our" in this Privacy Policy) refers to Dealer Pro Edge, LLC, 142 Swendsen Drive, Monroe, CT 06468, United States.
- **Cookies** are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.
- **Country** refers to: Connecticut, United States.
- **Device** means any device that can access the Service such as a computer, a cell phone or a digital tablet.
- **Personal Data** (or "Personal Information") is any information that relates to an identified or identifiable individual.
- **Service** refers to the Website.
- **Service Provider** means any natural or legal person who processes the data on behalf of the Company.
- **Usage Data** refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.
- **Website** refers to Dealer Pro Edge, accessible from https://dealerproedge.com.
- **You** means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.

## 1. Information We Collect

### Personal Data
While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. This may include, but is not limited to:

- Email address
- First name and last name
- Phone number
- Billing address and payment-related information (processed through secure third-party providers)
- Business or company information
- Account credentials
- Support requests, inquiries, or correspondence
- Any other information You choose to provide

### Usage Data
Usage Data is collected automatically when using the Service. Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.

### Cookies and Similar Technologies
We use Cookies and similar tracking technologies (including web beacons, pixels, tags, and scripts) to track the activity on Our Service and store certain information. We use both Session and Persistent Cookies for the purposes set out below.

**Necessary / Essential Cookies**  
Type: Session Cookies  
Administered by: Us  
Purpose: These Cookies are essential to provide You with services available through the Website and to enable You to use some of its features.

**Cookies Policy / Notice Acceptance Cookies**  
Type: Persistent Cookies  
Administered by: Us  
Purpose: These Cookies identify if users have accepted the use of cookies on the Website.

**Functionality Cookies**  
Type: Persistent Cookies  
Administered by: Us  
Purpose: These Cookies allow Us to remember choices You make when You use the Website.

Where required by law, we use non-essential cookies (such as analytics, advertising, and remarketing cookies) only with Your consent. You can withdraw or change Your consent at any time using Our cookie preferences tool (if available) or through Your browser/device settings.

## 2. How We Use Your Personal Data
We may use Personal Data for the following purposes:

- To provide and maintain our Service, including to monitor the usage of our Service.
- To manage Your Account and provide access to different functionalities.
- For the performance of a contract (including processing Orders and Subscriptions).
- To contact You by email, telephone, SMS, or push notifications regarding updates, security, or contracted services.
- To provide You with news, special offers, and general information about other goods, services and events which We offer that are similar to those that you have already purchased or inquired about unless You have opted not to receive such information.
- To manage Your requests and provide customer support.
- For business transfers or internal analysis, identifying usage trends, and improving our Service.
- To comply with legal obligations.

## 3. How We Share Your Personal Data
We may share Your Personal Data in the following situations:

- **With Service Providers**: to monitor and analyze the use of our Service, to contact You, process payments, host data, or provide support.
- **With Affiliates**: in which case we will require those affiliates to honor this Privacy Policy.
- **With business partners**: to offer You certain products, services or promotions.
- **For business transfers**: in connection with a merger, acquisition, or sale of assets.
- **For legal and safety reasons**: when required by law, to protect rights or safety, investigate fraud, or enforce our agreements.
- **With Your consent**: for any other purpose.

We do not sell personal data in the traditional sense. Categories of third parties we may share data with include service providers (hosting, analytics, payment processors, support tools) and affiliates.

## 4. Retention of Your Personal Data
The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.

Where possible, We apply shorter retention periods and/or reduce identifiability by deleting, aggregating, or anonymizing data.

**Examples of retention periods (maximums – we may delete sooner):**
- **Account Information**: for the duration of your account relationship plus up to 24 months after account closure.
- **Customer Support Data**: support tickets and correspondence up to 24 months from closure; chat transcripts up to 24 months.
- **Usage Data**: website analytics, cookies, IP addresses, and server logs up to 24 months from collection.

We may retain Personal Data longer for legal compliance, fraud prevention, or security. We securely delete or anonymize data when retention periods expire.

## 5. International Transfers
Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of Your state or country. We will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy, including appropriate safeguards where required by law.

## 6. Your Choices and Rights
You may update, amend, or delete Your information at any time by signing in to Your Account and visiting the account settings section, or by contacting Us.

Depending on your location (including Connecticut residents under the Connecticut Data Privacy Act), you may have rights to access, correct, delete, or obtain a copy of your personal information, or to opt out of certain processing. We will respond to verified requests in accordance with applicable law and provide a way to appeal any denial.

You can opt out of marketing communications using the unsubscribe link in our emails. We may still send transactional or service-related messages.

To exercise rights or request information about our data practices, contact us (see Section 11). We may need to verify your identity before processing certain requests.

## 7. Security of Your Personal Data
The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially reasonable means to protect Your Personal Data, We cannot guarantee its absolute security.

## 8. Children’s Privacy
Our Service does not address anyone under the age of 16. We do not knowingly collect personally identifiable information from anyone under the age of 16. If We become aware that We have collected Personal Data from anyone under the age of 16 without verification of parental consent (where required), We take steps to remove that information from Our servers.

## 9. Links to Other Websites
Our Service may contain links to other websites that are not operated by Us. We strongly advise You to review the Privacy Policy of every site You visit. We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.

## 10. Changes to this Privacy Policy
We may update Our Privacy Policy from time to time. We will notify You of any material changes by posting the new Privacy Policy on this page and, where appropriate, via email or a prominent notice on Our Service. Changes are effective when they are posted.

## 11. Contact Us
If you have any questions about this Privacy Policy, You can contact us:

**Dealer Pro Edge, LLC**  
142 Swendsen Drive  
Monroe, CT 06468  
United States  

Email: privacy@dealerproedge.com
`

// =============================================================================
// LOADER
// =============================================================================

export async function loader() {
	const html = await marked.parse(markdown)
	return data({ html })
}

// =============================================================================
// ROUTE COMPONENT
// =============================================================================

export default function DealerWebsitePrivacyPolicyRoute({
	loaderData,
}: Route.ComponentProps) {
	return (
		<main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
			<article
				className="prose prose-neutral max-w-none [&_h2]:mb-2 [&_h2]:mt-5"
				dangerouslySetInnerHTML={{ __html: loaderData.html }}
			/>
		</main>
	)
}
